import {createSlice} from '@reduxjs/toolkit'
import ResourceService from '../../api/resource'
import Statistics from '../../api/statistics'
import {doNothing} from '../../utils/placeholder'
import text from '../text.json'
import {toast} from 'react-toastify'
import {routes} from '../../routes'
import DateHelper from '../../utils/date'
import TextHelper from '../../utils/text'
import roundValue from '../../utils/math'
import FormHelper from '../../utils/forms'
import statusCodes from '../../utils/http'
import serverMetrics from '../../data/serverMetrics.json'

const EXCLUDED_TYPES = ['free_gb', 'n_cpus', 'uptime']
const UTC = 'UTC'
const UNIFIED_TYPE = 'unified'
const CPU_TOTAL = 'cpu-total'

const getMetricTypeValues = (serverResource) => {
    return Object.values(serverResource?.grouped_metric_types || {})
        .flatMap(metric => metric)
        ?.filter(value => value !== null && value !== undefined)
}

const getMetricTypeStateObjects = (serverResource) => {
    const obj = {}
    const metricValues = getMetricTypeValues(serverResource)
    for (let i = 0; i < metricValues.length; i++) {
        obj[metricValues[i]] = {
            commonMetricsInfo: {},
            absoluteAdditionalMetrics: {},
            categories: [],
            metricsData: [],
            groupedData: {},
            additionalMetricsData: []
        }
    }
    return obj
}

export const serverMonitoringSlice = createSlice({
    name: 'server-monitoring',
    initialState:  {
        serverResource: {},
        isResourceFetching: false,
        isButtonLoading: false,
        isMetricsDataRetrieving: {},
        metricSources: [],
        rulesets: [],
        serverResourceId: '',
        ...getMetricTypeStateObjects({})
    },
    reducers: {
        setServerResource: (state, {payload}) => {
            state.serverResource = payload.data
            if (payload.isConfigured) {
                return
            }
            const metricTypeObjects = getMetricTypeStateObjects(payload.data)
            Object.assign(state, metricTypeObjects)
        },
        setServerResourceId: (state, {payload}) => {
            state.serverResourceId = payload
        },
        setIsResourceFetching: (state, {payload}) => {
            state.isResourceFetching = payload
        },
        setIsButtonLoading: (state, {payload}) => {
            state.isButtonLoading = payload
        },
        setIsMetricsDataRetrieving: (state, {payload}) => {
            const {type, isLoading} = payload
            state.isMetricsDataRetrieving = {
                ...state.isMetricsDataRetrieving,
                [type]: isLoading
            }
        },
        setCommonMetricsInfo: (state, {payload}) => {
            const {type, data} = payload
            state[type].commonMetricsInfo = data
        },
        setMetricsData: (state, {payload}) => {
            const {type, data} = payload
            state[type].metricsData = data
        },
        setMetricSources: (state, {payload}) => {
            state.metricSources = payload
        },
        setAbsoluteAdditionalMetrics: (state, {payload}) => {
            const {type, data} = payload
            const filteredWidgetTypes = state[type].metricsData?.[state[type].metricsData?.length - 1]?.extra?.filter(data => EXCLUDED_TYPES?.includes(data?.type))
            const additionalMetrics = {}

            if (state[type].commonMetricsInfo?.unit === UNIFIED_TYPE && filteredWidgetTypes?.length > 0) {
                filteredWidgetTypes.map(currentWidget => {
                    const {value, type: widgetType} = currentWidget
                    additionalMetrics[widgetType] = {
                        value: Number.isFinite(value) ? roundValue(value, 2) : value,
                        unit: currentWidget.unit
                    }
                })
                state[type].absoluteAdditionalMetrics = additionalMetrics
                return
            }

            data && Object.keys(data).map(key => {
                additionalMetrics[key] = {value: roundValue(data[key]), unit: state[type].commonMetricsInfo.unit}
            })
            state[type].absoluteAdditionalMetrics = additionalMetrics
        },
        setCategories: (state, {payload}) => {
            const {type, data} = payload
            state[type].categories = data
        },
        setGroupedData: (state, {payload}) => {
            const {type, data} = payload
            state[type].groupedData = data
        },
        setAdditionalMetricsData: (state, {payload}) => {
            const {type, data} = payload
            state[type].additionalMetricsData = data
        },
        setRulesets: (state, {payload}) => {
            state.rulesets = [
                ...state.rulesets,
                payload
            ]
        }
    }
})

export const {
    setServerResource,
    setServerResourceId,
    setIsResourceFetching,
    setIsButtonLoading,
    setIsMetricsDataRetrieving,
    setCommonMetricsInfo,
    setAbsoluteAdditionalMetrics,
    setCategories,
    setMetricsData,
    setGroupedData,
    setAdditionalMetricsData,
    setMetricSources,
    setRulesets
} = serverMonitoringSlice.actions

export default serverMonitoringSlice.reducer

const ERROR_MSG = 'An error occured. Please try again later'
export const addServerResource = (payload) => {
    return async dispatch => {
        dispatch(setIsButtonLoading(true))
        try {
            const {data} = await ResourceService.addServerResource(payload)
            dispatch(setServerResourceId(data?.data?.server_resource?.id))
            return {success: true}
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message || ERROR_MSG
            FormHelper.setFormElementError(fields, message)
            return {success: false}
        } finally {
            dispatch(setIsButtonLoading(false))
        }
    }
}

export const getServerResource = (showLoader = false, id, navigate, isEdit = false, isConfigured = false) => {
    return async(dispatch) => {
        showLoader && dispatch(setIsResourceFetching(true))
        try {
            const {data} = await ResourceService.getServerResource(id)
            await dispatch(setServerResource({
                data: data?.data?.server_resource,
                isConfigured
            }))
            !isEdit && await dispatch(getServerResourceMetrics(showLoader))
        } catch (e) {
            const res = e?.response
            if (statusCodes.SpecialErrors.includes(res?.status)) {
                navigate(routes.not_found.path)
            }
        }
        showLoader && dispatch(setIsResourceFetching(false))
    }
}

export const updateServerResource = (payload, isEdit = false, id = null, showMsg = true) => {
    return async(dispatch, getState) => {
        dispatch(setIsButtonLoading(true))
        try {
            const resourceId = id || getState().server_monitoring.serverResourceId
            const {data} = await ResourceService.updateServerResource(resourceId, payload)
            const successMessage = !id && !isEdit
                ? text.create_success_msg
                : (isEdit ? data?.message : text.update_server_resource_status_msg)

            showMsg && toast.success(successMessage)
            return {success: true}
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message || ERROR_MSG
            FormHelper.setFormElementError(fields, message)
            return {success: false}
        } finally {
            dispatch(setIsButtonLoading(false))
        }
    }
}

export const getServerResourceMetrics = (showLoader = false, query = null, metricType = null) => {
    return async(dispatch, getState) => {
        const serverResource = getState().server_monitoring.serverResource
        const metricValues = getMetricTypeValues(serverResource)

        if (showLoader) {
            metricType ? dispatch(setIsMetricsDataRetrieving({type: metricType, isLoading: true}))
                : metricValues.forEach(type => {
                    dispatch(setIsMetricsDataRetrieving({type, isLoading: true}))
                })
        }

        try {
            const fetchMetricsData = async(type) => {
                const modifiedQuery = `${query ?
                    `?type=${type}&${query}` :
                    `?type=${type}`}${type === serverMetrics.processes_unified.value && !metricType ? `&limit=1` : ''}`
                const {data} = await Statistics.getServerResourceMetrics(serverResource?.id, modifiedQuery)
                return data?.data || {}
            }

            if (metricType) {
                const data = await fetchMetricsData(metricType)
                const categories = await dispatch(updateCategoriesAction(metricType, true, data.metrics))
                const updatedGroupedData = groupMetricsData(data.metrics, metricType)
                showLoader && dispatch(setIsMetricsDataRetrieving({type: metricType, isLoading: false}))

                return {categories, groupedData: updatedGroupedData}
            }

            await Promise.all(metricValues.map(async(type) => {
                const data = await fetchMetricsData(type)

                if (!data?.common) {
                    await dispatch(setCommonMetricsInfo({type, data: null}))
                    await dispatch(setAbsoluteAdditionalMetrics({type, data: null}))
                    await dispatch(setCategories({type, data: []}))
                    await dispatch(setMetricsData({type, data: []}))
                    await dispatch(setGroupedData({type, data: {}}))
                    await dispatch(readyData(type))
                    showLoader && dispatch(setIsMetricsDataRetrieving({type, isLoading: false}))
                    return
                }

                await dispatch(setCommonMetricsInfo({type, data: data?.common}))
                await dispatch(setMetricsData({type, data: data.metrics}))
                await dispatch(readyData(type))
                await dispatch(setAbsoluteAdditionalMetrics({type, data: data?.absolute_metric_additional}))
                type === serverMetrics.network_usage_unified.value && await dispatch(setMetricSources(data?.metric_sources))
                showLoader && dispatch(setIsMetricsDataRetrieving({type, isLoading: false}))
            }))
        } catch {
            doNothing()
        }
    }
}

const readyData = (type) => {
    return async(dispatch) => {
        await dispatch(updateCategoriesAction(type))
        await dispatch(updateAdditionalMetricsAction(type))
        await dispatch(updateGroupedDataAction(type))
    }
}

const updateCategoriesAction = (type, shouldReturnData = false, newMetricsData = null) => {
    return async(dispatch, getState) => {
        try {
            const state = getState().server_monitoring
            const resourceTimezone = state.serverResource?.timezone === UTC ? UTC : state.serverResource?.timezone
            const options = {
                timeZone: resourceTimezone,
                hour12: false,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }
            const convertToTimezone = (date) => DateHelper.convertToTimeZone(new Date(date).toLocaleString('en-US', options))
            const {metricsData} = state[type]
            const data = (newMetricsData || metricsData).map(metric => convertToTimezone(metric?.datetime))

            if (shouldReturnData) {
                return data
            }

            dispatch(setCategories({type, data}))
        } catch {
            doNothing()
        }
    }
}

const groupMetricsData = (metricsData, type, additionalMetricsData) => {
    const {charts} = Object.values(serverMetrics).find(metric => metric.value === type)
    const result = {}

    Object.entries(charts).forEach(([chartKey, chartMeta]) => {
        const {chart_key: chartType, grouping} = chartMeta

        metricsData.forEach(({value, extra = []}) => {
            if (!grouping) {
                handleDefaultGrouping(result, chartMeta, value, additionalMetricsData)
                return
            }

            extra.forEach(extraItem => {
                if (!isValidExtra(extraItem, chartMeta, grouping, chartType)) return

                const key = getGroupKey(grouping)
                const keyValue = extraItem[key]

                result[chartKey] = result[chartKey] || []
                let group = result[chartKey].find(item => item[key] === keyValue)

                if (!group) {
                    group = createGroup(extraItem, key)
                    result[chartKey].push(group)
                }

                group.data.push(roundValue(extraItem.value))
            })
        })

        if (isEmptyActiveUsersChart(chartKey, result)) {
            delete result[chartKey]
        }

        if (type === serverMetrics.cpu_total_usage.value) {
            result[chartKey] = result[chartKey].map(item =>
                ({
                    ...item.name === CPU_TOTAL
                        ? {...item, name: serverMetrics.cpu_total_usage.charts?.[chartKey].total_cpu_name}
                        : item
                })
            )
        }

        if (!grouping && additionalMetricsData?.length > 0) {
            result.default = result.default.concat(additionalMetricsData)
        }
    })
    return result
}

const handleDefaultGrouping = (result, chartMeta, value) => {
    result.default = result.default || [{data: [], unit: chartMeta.unit}]
    result.default[0].data.push(roundValue(value))
}

const isValidExtra = (extra, chartMeta, grouping, chartType) => {
    if (EXCLUDED_TYPES.includes(extra.type) ||
    (grouping === serverMetrics.disk_usage_unified.charts.usage_gb.grouping && extra.type === 'total')) return false
    if (grouping === serverMetrics.cpu_total_usage.charts.usage.grouping && extra.type !== chartType) return false
    if ((grouping === serverMetrics.memory_usage.charts.total_used.grouping ||
        grouping !== serverMetrics.cpu_total_usage.charts.usage.grouping) && extra.unit !== chartMeta.unit) return false
    return true
}

const getGroupKey = (grouping) => grouping === serverMetrics.memory_usage.charts.total_used.grouping ?
    serverMetrics.memory_usage.charts.total_used.grouping : 'name'

const createGroup = (extra, keyField) => ({
    [keyField]: extra[keyField],
    unit: extra.unit,
    data: []
})

const isEmptyActiveUsersChart = (chartKey, result) => {
    const activeChartKey = serverMetrics.system_load_unified.charts.active_users.chart_key
    if (chartKey !== activeChartKey) return false

    return !result[chartKey]?.some(group => group.data?.some(value => value !== 0))
}

const updateGroupedDataAction = (type) => {
    return async(dispatch, getState) => {
        try {
            const {metricsData, additionalMetricsData} = getState().server_monitoring[type]
            const groupedData = groupMetricsData(metricsData, type, additionalMetricsData)
            dispatch(setGroupedData({type, data: groupedData}))
        } catch {
            doNothing()
        }
    }
}

const getAdditionalMetricsData = (metricsData, commonMetricsInfo) => {
    const additionalData = {}
    metricsData
        ?.filter((data) => data.metric_additional)
        ?.forEach((data) => {
            Object.keys(data.metric_additional).forEach((key) => {
                if (key !== 'avg') {
                    additionalData[key] = additionalData[key] || {name: TextHelper.capitalizeFirstChar(key), data: [], unit: commonMetricsInfo?.unit}
                    additionalData[key].data.push(roundValue(data.metric_additional[key]))
                }
            })
        })

    return Object.values(additionalData)
}

const updateAdditionalMetricsAction = (type) => {
    return async(dispatch, getState) => {
        try {
            const {metricsData, commonMetricsInfo} = getState().server_monitoring[type]
            const additionalMetricsData = getAdditionalMetricsData(metricsData, commonMetricsInfo)
            dispatch(setAdditionalMetricsData({type, data: additionalMetricsData}))
        } catch (e) {
            doNothing()
        }
    }
}
